/**
 * SyncService - Handles data synchronization and persistence
 */
class SyncService {
  constructor() {
    this.syncInterval = null;
    this.isOnline = navigator.onLine;
    this.pendingChanges = new Map();
    this.lastSyncTimestamp = 0;
    
    // Listen to online/offline events
    window.addEventListener('online', this.handleOnline.bind(this));
    window.addEventListener('offline', this.handleOffline.bind(this));
  }

  /**
   * Initialize sync service with user context
   * @param {Object} user - Current user
   * @param {Object} dataContext - Data context for managing state
   * @param {Object} apiService - API service for backend communication
   */
  initialize(user, dataContext, apiService) {
    this.user = user;
    this.dataContext = dataContext;
    this.apiService = apiService;
    this.startPeriodicSync();
    this.loadPendingChanges();
  }

  /**
   * Start periodic synchronization
   */
  startPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
    }

    // Sync every 2 minutes when online
    this.syncInterval = setInterval(() => {
      if (this.isOnline) {
        this.syncPendingChanges();
      }
    }, 120000); // 2 minutes
  }

  /**
   * Stop periodic synchronization
   */
  stopPeriodicSync() {
    if (this.syncInterval) {
      clearInterval(this.syncInterval);
      this.syncInterval = null;
    }
  }

  /**
   * Handle online event
   */
  handleOnline() {
    this.isOnline = true;
    console.log('Back online - syncing pending changes...');
    this.syncPendingChanges();
  }

  /**
   * Handle offline event
   */
  handleOffline() {
    this.isOnline = false;
    console.log('Gone offline - changes will be queued for sync');
  }

  /**
   * Queue a change for synchronization
   * @param {string} type - Type of change (create, update, delete)
   * @param {string} entity - Entity type (line, truck, pallet, load)
   * @param {Object} data - Change data
   * @param {string} id - Unique identifier for the change
   */
  queueChange(type, entity, data, id = null) {
    const changeId = id || `${type}_${entity}_${Date.now()}_${Math.random()}`;
    const change = {
      id: changeId,
      type,
      entity,
      data,
      timestamp: Date.now(),
      retryCount: 0
    };

    this.pendingChanges.set(changeId, change);
    this.savePendingChanges();

    // Try to sync immediately if online
    if (this.isOnline) {
      this.syncPendingChanges();
    }

    return changeId;
  }

  /**
   * Sync all pending changes
   */
  async syncPendingChanges() {
    if (!this.isOnline || this.pendingChanges.size === 0) {
      return;
    }

    console.log(`Syncing ${this.pendingChanges.size} pending changes...`);
    const changes = Array.from(this.pendingChanges.values());
    const successfulSyncs = [];

    for (const change of changes) {
      try {
        await this.syncSingleChange(change);
        successfulSyncs.push(change.id);
      } catch (error) {
        console.error(`Failed to sync change ${change.id}:`, error);
        
        // Increment retry count
        change.retryCount++;
        
        // Remove changes that have failed too many times
        if (change.retryCount > 5) {
          console.warn(`Removing change ${change.id} after ${change.retryCount} failed attempts`);
          successfulSyncs.push(change.id);
        } else {
          this.pendingChanges.set(change.id, change);
        }
      }
    }

    // Remove successfully synced changes
    successfulSyncs.forEach(id => this.pendingChanges.delete(id));
    this.savePendingChanges();

    if (successfulSyncs.length > 0) {
      console.log(`Successfully synced ${successfulSyncs.length} changes`);
    }
  }

  /**
   * Sync a single change
   * @param {Object} change - The change to sync
   */
  async syncSingleChange(change) {
    const { type, entity, data } = change;

    switch (entity) {
      case 'line':
        return this.syncLineChange(type, data);
      case 'truck':
        return this.syncTruckChange(type, data);
      case 'pallet':
        return this.syncPalletChange(type, data);
      case 'load':
        return this.syncLoadChange(type, data);
      default:
        throw new Error(`Unknown entity type: ${entity}`);
    }
  }

  /**
   * Sync line changes
   */
  async syncLineChange(type, data) {
    switch (type) {
      case 'create':
        return await this.apiService.createLine(data);
      case 'update':
        return await this.apiService.updateLine(data.line_id, data);
      case 'delete':
        return await this.apiService.deleteLine(data.line_id);
      default:
        throw new Error(`Unknown change type: ${type}`);
    }
  }

  /**
   * Sync truck changes
   */
  async syncTruckChange(type, data) {
    switch (type) {
      case 'create':
        return await this.apiService.createTruck(data.line_id, data);
      case 'update':
        return await this.apiService.updateTruck(data.line_id, data.truck_id, data);
      case 'delete':
        return await this.apiService.deleteTruck(data.line_id, data.truck_id);
      default:
        throw new Error(`Unknown change type: ${type}`);
    }
  }

  /**
   * Sync pallet changes
   */
  async syncPalletChange(type, data) {
    switch (type) {
      case 'create':
        return await this.apiService.createPallet(data.line_id, data.truck_id, data);
      case 'update':
        return await this.apiService.updatePallet(data.line_id, data.truck_id, data.pallet_id, data);
      case 'delete':
        return await this.apiService.deletePallet(data.line_id, data.truck_id, data.pallet_id);
      default:
        throw new Error(`Unknown change type: ${type}`);
    }
  }

  /**
   * Sync load changes
   */
  async syncLoadChange(type, data) {
    switch (type) {
      case 'create':
        return await this.apiService.saveLoad(data);
      case 'update':
        return await this.apiService.updateLoad(data.id, data);
      case 'delete':
        return await this.apiService.deleteLoad(data.id);
      default:
        throw new Error(`Unknown change type: ${type}`);
    }
  }

  /**
   * Save pending changes to localStorage
   */
  savePendingChanges() {
    if (!this.user) return;

    const changes = Array.from(this.pendingChanges.entries());
    localStorage.setItem(`pending_changes_${this.user.id}`, JSON.stringify(changes));
  }

  /**
   * Load pending changes from localStorage
   */
  loadPendingChanges() {
    if (!this.user) return;

    try {
      const stored = localStorage.getItem(`pending_changes_${this.user.id}`);
      if (stored) {
        const changes = JSON.parse(stored);
        this.pendingChanges = new Map(changes);
      }
    } catch (error) {
      console.error('Error loading pending changes:', error);
      this.pendingChanges.clear();
    }
  }

  /**
   * Clear all pending changes (usually called on logout)
   */
  clearPendingChanges() {
    this.pendingChanges.clear();
    if (this.user) {
      localStorage.removeItem(`pending_changes_${this.user.id}`);
    }
  }

  /**
   * Force a full data refresh from the server
   */
  async forceRefresh() {
    if (!this.isOnline || !this.dataContext) {
      throw new Error('Cannot refresh while offline');
    }

    console.log('Force refreshing all data...');
    await this.dataContext.loadAllUserData();
  }

  /**
   * Get sync status
   */
  getSyncStatus() {
    return {
      isOnline: this.isOnline,
      pendingChanges: this.pendingChanges.size,
      lastSyncTimestamp: this.lastSyncTimestamp,
      isSyncing: this.isSyncing || false
    };
  }

  /**
   * Cleanup on service destruction
   */
  destroy() {
    this.stopPeriodicSync();
    window.removeEventListener('online', this.handleOnline.bind(this));
    window.removeEventListener('offline', this.handleOffline.bind(this));
  }
}

export const syncService = new SyncService();
export default syncService;
